import { IDBPDatabase, openDB } from "idb";

import type {
  AudioDB,
  RecordingChunks,
  RecordingData
} from "./index-db-types.type";

export const MEDICAL_SCRIBE_DB_NAME = "medical-transcribes";
export const DB_SCHEMA_VERSION = 7; // Added recording_sessions store for session persistence

export async function resetDB(): Promise<void> {
  console.log("Attempting to delete database:", MEDICAL_SCRIBE_DB_NAME);
  try {
    await indexedDB.deleteDatabase(MEDICAL_SCRIBE_DB_NAME);
    console.log("Database deleted successfully");
  } catch (error) {
    console.error("Error deleting database:", error);
  }
}

function verifyStores(db: IDBPDatabase<AudioDB>): boolean {
  const requiredStores = [
    "recordings",
    "recordings_chunks",
    "recording_sessions"
  ] as const;
  const existingStores = Array.from(db.objectStoreNames);
  return requiredStores.every((store) => existingStores.includes(store));
}

export async function getDB() {
  try {
    const db = await openDB<AudioDB>(
      MEDICAL_SCRIBE_DB_NAME,
      DB_SCHEMA_VERSION,
      {
        upgrade(db, oldVersion, newVersion, transaction) {
          console.log(
            `Upgrading database from version ${oldVersion} to ${newVersion}`
          );

          // Delete existing stores if they exist
          if (db.objectStoreNames.contains("recordings")) {
            console.log("Deleting existing recordings store");
            db.deleteObjectStore("recordings");
          }
          if (db.objectStoreNames.contains("recordings_chunks")) {
            console.log("Deleting existing recordings_chunks store");
            db.deleteObjectStore("recordings_chunks");
          }
          if (db.objectStoreNames.contains("recording_sessions")) {
            console.log("Deleting existing recording_sessions store");
            db.deleteObjectStore("recording_sessions");
          }

          // Create fresh stores
          console.log("Creating recordings store");
          const recordingsStore = db.createObjectStore("recordings", {
            keyPath: "id"
          });
          recordingsStore.createIndex("uploadStatus", "uploadStatus");

          console.log("Creating recordings_chunks store");
          db.createObjectStore("recordings_chunks", { keyPath: "id" });

          console.log("Creating recording_sessions store");
          db.createObjectStore("recording_sessions", { keyPath: "id" });

          console.log("Database upgrade completed successfully");
        },
        blocked() {
          console.warn(
            "Database upgrade blocked - another tab may be using the database"
          );
        },
        blocking() {
          console.warn("This tab is blocking a database upgrade");
        },
        terminated() {
          console.error("Database connection terminated unexpectedly");
        }
      }
    );

    // Verify stores exist after opening
    if (!verifyStores(db)) {
      console.error("Database opened but required stores are missing");
      throw new Error("Database opened but required stores are missing");
    }
    return db;
  } catch (error) {
    console.error("Error opening database:", error);
    // If there's an error, try resetting the database once
    console.log("Attempting database reset and recreation");
    try {
      await resetDB();

      console.log("Creating fresh database");
      const freshDb = await openDB<AudioDB>(
        MEDICAL_SCRIBE_DB_NAME,
        DB_SCHEMA_VERSION,
        {
          upgrade(db) {
            console.log("Creating fresh stores");
            const recordingsStore = db.createObjectStore("recordings", {
              keyPath: "id"
            });
            db.createObjectStore("recordings_chunks", { keyPath: "id" });
            recordingsStore.createIndex("uploadStatus", "uploadStatus");
            console.log("Fresh stores created");
          }
        }
      );

      // Verify stores exist after recreation
      if (!verifyStores(freshDb)) {
        throw new Error(
          "Fresh database created but required stores are missing"
        );
      }

      return freshDb;
    } catch (recreationError) {
      console.error(
        "Failed to recreate database after reset:",
        recreationError
      );
      const errorMessage =
        recreationError instanceof Error
          ? recreationError.message
          : "Unknown error during database recreation";
      throw new Error(`Database initialization failed: ${errorMessage}`);
    }
  }
}

// One connection for the recording worker's chunk writes (SN-20000, behind
// AMBIENT_SCRIBE_REUSE_IDB_CONNECTION). getDB() opens a new connection on every call
// and nothing closes them, so a long recording left hundreds open on the origin.
// Module state is per realm: only the worker that calls this shares the connection.
let sharedDB: Promise<IDBPDatabase<AudioDB>> | null = null;

export function getSharedDB(): Promise<IDBPDatabase<AudioDB>> {
  if (sharedDB) return sharedDB;
  const opening = getDB();
  sharedDB = opening;
  opening.then(
    (db) => {
      // `close` fires only when the browser shuts the connection (site data cleared,
      // a disk error). Forget it so the next call reopens, unless a newer one has
      // already replaced it.
      db.addEventListener("close", () => {
        if (sharedDB === opening) sharedDB = null;
      });
    },
    () => {
      // The caller still gets this rejection; only the cache forgets it.
      if (sharedDB === opening) sharedDB = null;
    }
  );
  return opening;
}

// For a write that failed on the shared connection: a connection the browser broke
// without a `close` event would fail every later write, so the next call opens a
// fresh one, as every call does with the flag off.
export function forgetSharedDB() {
  sharedDB = null;
}

export async function getAllFromTable<T extends keyof AudioDB>(
  tableName: T
): Promise<AudioDB[T]["value"][]> {
  const db = await getDB();
  const tx = db.transaction(tableName as any, "readonly");
  const store = tx.objectStore(tableName);
  return await store.getAll();
}

export async function getByIdFromTable<T extends keyof AudioDB>(
  tableName: T,
  id: string
): Promise<AudioDB[T]["value"] | undefined> {
  const db = await getDB();
  const tx = db.transaction(tableName as any, "readonly");
  const store = tx.objectStore(tableName);
  return store.get(id);
}

export async function getAllRecordings(): Promise<RecordingData[]> {
  return getAllFromTable("recordings");
}

export async function getRecordingById(
  id: string
): Promise<RecordingData | undefined> {
  return getByIdFromTable("recordings", id);
}

export async function updateRecordingById(
  id: string,
  modifierFn: (record: RecordingData) => RecordingData
): Promise<RecordingData | undefined> {
  const db = await getDB();
  const tx = db.transaction("recordings", "readwrite");
  const store = tx.objectStore("recordings");
  const record = await store.get(id);

  if (record) {
    const newValue = modifierFn(record);
    await store.put(newValue);
    await tx.done;
    return newValue;
  }
}

export async function deleteChunksById(id: string): Promise<undefined> {
  const db = await getDB();
  const tx = db.transaction(["recordings_chunks"], "readwrite");
  const chunks = tx.objectStore("recordings_chunks");
  const record = await chunks.get(id);
  if (!record) return;
  await tx.objectStore("recordings_chunks").delete(id);
  await tx.done;
}

export async function deleteRecordingById(id: string): Promise<undefined> {
  const db = await getDB();
  const tx = db.transaction(["recordings", "recordings_chunks"], "readwrite");
  const recordings = tx.objectStore("recordings");
  const record = await recordings.get(id);

  if (!record) return;

  await tx.objectStore("recordings_chunks").delete(id);
  await tx.objectStore("recordings").delete(id);
  await tx.done;
}

export async function getAllChunks(): Promise<RecordingChunks[]> {
  return getAllFromTable("recordings_chunks");
}

export async function getChunkById(
  id: string
): Promise<RecordingChunks | undefined> {
  return getByIdFromTable("recordings_chunks", id);
}
